import { FormRow, FormSection, BigFormRow } from 'framework/ui/layout';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CreateForm, Dropdown, TextArea, useFormContext } from 'framework/form';
import useRedmineUrl from 'infrastructure/integrations/redmine/useRedmineUrl';
import { Alert, Box, Typography } from '@mui/material';
import { ShowDate } from 'framework/ui/ui-builder';
import { DATE_FORMAT } from 'framework/utils';
import { OneStopModal, ModalData } from 'domain/shared/oneStop';
import { OneStopPageLayout } from 'domain/shared/oneStop';
import {
  TimeTrackChangeRequestDto,
  TimeTrackChangeRequestStatus,
  TimeTrackRequestType,
  TimeTrackActivity,
  WorkloadChangeItem,
} from './types';
import { requestTypeOptions, activityOptions } from './constants';
import { TextField, LinkField } from 'framework/ui/field';
import useTimeTrackChangeRequestService from 'domain/gpo/oneStop/workloadReportingIssues/useTimeTrackChangeRequestService';
import { Loading } from 'framework/ui/mui-builder';

const getActivityLabel = (activity?: TimeTrackActivity): string => {
  const option = activityOptions.find(opt => opt.value === activity);
  return option?.label || activity || '';
};

const formatDate = (dateString?: string): React.ReactNode => {
  if (!dateString) return '-';
  return <ShowDate dateUTC={dateString} format={DATE_FORMAT.SIMPLE} isDateTime={false} />;
};

const REVIEWED_STATUS_LABELS: Partial<Record<TimeTrackChangeRequestStatus, string>> = {
  [TimeTrackChangeRequestStatus.APPROVED]: 'approved',
  [TimeTrackChangeRequestStatus.APPLIED]: 'approved',
  [TimeTrackChangeRequestStatus.FAILED]: 'approved',
  [TimeTrackChangeRequestStatus.REJECTED]: 'rejected',
};

const isReviewed = (status?: TimeTrackChangeRequestStatus): boolean =>
  !!status && status !== TimeTrackChangeRequestStatus.PENDING_REVIEW;

const ReviewedNotice: React.FC<{ changeRequest: TimeTrackChangeRequestDto }> = ({ changeRequest }) => {
  const label = (changeRequest.status && REVIEWED_STATUS_LABELS[changeRequest.status]) || 'reviewed';

  return (
    <Alert severity="info" sx={{ mb: 3 }}>
      This request has already been {label}
      {changeRequest.reviewedAt ? (
        <>
          {' on '}
          {formatDate(changeRequest.reviewedAt)}
        </>
      ) : null}
      .{changeRequest.rejectionReason ? ` Reason: ${changeRequest.rejectionReason}` : ''}
    </Alert>
  );
};

const TicketLink: React.FC<{ ticketId?: number }> = ({ ticketId }) => {
  const { getIssueUrl } = useRedmineUrl();

  if (!ticketId) {
    return (
      <Typography component="span" variant="body1">
        -
      </Typography>
    );
  }

  return <LinkField linkText={`#${ticketId}`} value={getIssueUrl(ticketId)} isInternal={false} />;
};

const IncorrectTimeEntryChange: React.FC<{ change: WorkloadChangeItem; index: number }> = ({ change }) => {
  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        border: '1px solid',
        borderColor: 'border.default',
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
        <TicketLink ticketId={change.ticketId} />
        <Typography component="span" variant="body1">
          ({change.currentTimeTrack || 0} h)
        </Typography>
        <Typography component="span" variant="body1">
          on
        </Typography>
        {formatDate(change.trackingDate)}
        <Typography component="span" variant="body1">
          →
        </Typography>
        <TicketLink ticketId={change.correctTicketId} />
        <Typography component="span" variant="body1">
          ({change.correctTimeTrack || 0} h)
        </Typography>
        <Typography component="span" variant="body1">
          on
        </Typography>
        {formatDate(change.correctTrackingDate)}
      </Box>
    </Box>
  );
};

const MissedTrackingChange: React.FC<{ change: WorkloadChangeItem; index: number }> = ({ change }) => {
  return (
    <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'border.default', borderRadius: 1 }}>
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
        <TicketLink ticketId={change.correctTicketId} />
        <Typography component="span" variant="body1">
          -
        </Typography>
        {formatDate(change.correctTrackingDate)}
        <Typography component="span" variant="body1">
          -
        </Typography>
        <Typography component="span" variant="body1">
          {change.correctTimeTrack || 0} h
        </Typography>
        <Typography component="span" variant="body1">
          -
        </Typography>
        <Typography component="span" variant="body1">
          {getActivityLabel(change.activity)}
        </Typography>
      </Box>
      {change.descriptionOfWork && (
        <Box sx={{ mt: 1, color: 'text.secondary', fontSize: '0.875rem' }}>{change.descriptionOfWork}</Box>
      )}
    </Box>
  );
};

const ChangesListDisplay: React.FC<{ changes: WorkloadChangeItem[]; requestType: TimeTrackRequestType }> = ({
  changes,
  requestType,
}) => {
  const isMissedTracking = requestType === TimeTrackRequestType.NEW_TIME_ENTRY;

  if (changes.length === 0) {
    return <Box>No changes</Box>;
  }

  return (
    <Box>
      {changes.map((change, index) => {
        const uniqueKey = isMissedTracking
          ? `missed-${change.correctTicketId}-${change.correctTrackingDate}-${index}`
          : `incorrect-${change.timeEntryId || change.ticketId}-${index}`;
        return (
          <Box key={uniqueKey}>
            {isMissedTracking ? (
              <MissedTrackingChange change={change} index={index} />
            ) : (
              <IncorrectTimeEntryChange change={change} index={index} />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const ReviewReasonInput: React.FC = () => {
  const { data = {} } = useFormContext();
  const isApproved = (data as any).isApproved;
  const isRejected = isApproved === 'NO';

  if (!isRejected) {
    return null;
  }

  return (
    <FormRow>
      <TextArea source="reason" label="Reason" placeholder="Enter reason for rejection" isRequired={false} />
    </FormRow>
  );
};

const TimeTrackChangeRequestReview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getChangeRequest, reviewRequest, getValidationSchema } = useTimeTrackChangeRequestService();
  const [changeRequest, setChangeRequest] = useState<TimeTrackChangeRequestDto | null>(null);
  const [isLegacy, setIsLegacy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modalData, setModalData] = useState<Nullable<ModalData>>(null);

  useEffect(() => {
    if (id) {
      getChangeRequest(id)
        .then(resolved => {
          setChangeRequest(resolved.changeRequest);
          setIsLegacy(resolved.isLegacy);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return <Loading />;
  }

  if (!changeRequest) {
    return <Typography variant="body1">Change request not found</Typography>;
  }

  const getRequestTypeLabel = (type: TimeTrackRequestType): string => {
    const option = requestTypeOptions.find(opt => opt.value === type);
    return option?.label || type;
  };

  const getReasonLabel = (reason: string): string => {
    return reason || '';
  };

  const requestDetails = (
    <>
      <FormRow>
        <TextField source="requestType" label="Request type" value={getRequestTypeLabel(changeRequest.requestType)} />
        <TextField
          source="reasonForChange"
          label="Reason for change"
          value={getReasonLabel(changeRequest.reasonForChange as unknown as string)}
        />
      </FormRow>
      <Box sx={{ mt: 3 }}>
        <BigFormRow>
          <ChangesListDisplay changes={changeRequest.changes} requestType={changeRequest.requestType} />
        </BigFormRow>
      </Box>
    </>
  );

  if (isReviewed(changeRequest.status)) {
    return (
      <Box className="one-stop-page" position="relative">
        <FormSection title="Time track change request">
          <ReviewedNotice changeRequest={changeRequest} />
          {requestDetails}
        </FormSection>
      </Box>
    );
  }

  return (
    <>
      <CreateForm
        submitFormFunc={data => reviewRequest(data, isLegacy)}
        getValidationSchemaFunc={getValidationSchema}
        afterSubmit={{
          execute: () => {
            setModalData({
              isOpen: true,
              title: '',
              content:
                'Request has been successfully reviewed! Changes may take some time to be reflected in time tracking system.',
            });
          },
        }}
        initialData={{
          changeRequestId: changeRequest.id,
          isApproved: '',
          reason: '',
        }}
      >
        <OneStopPageLayout>
          <FormSection title="Time track change request">
            {requestDetails}
            <Box sx={{ mt: 3 }}>
              <FormRow>
                <Dropdown
                  source="isApproved"
                  label="Decision"
                  options={[
                    { value: 'YES', label: 'Approve' },
                    { value: 'NO', label: 'Reject' },
                  ]}
                  isRequired
                />
              </FormRow>
              <ReviewReasonInput />
            </Box>
          </FormSection>
        </OneStopPageLayout>
      </CreateForm>

      <OneStopModal
        data={modalData}
        isOneStop={false}
        handleMessageModalClose={() => {
          setModalData(null);
          navigate('/one-stop');
        }}
      />
    </>
  );
};

export default TimeTrackChangeRequestReview;
